import * as Yup from "yup";

import {
  KEKAddPercentage,
  KEKAddPercentageVariables,
} from "_graphql/kek/mutation/__generated__/KEKAddPercentage";
import { Modal, OfficeButton } from "components";
import { useAppNavigate, useAppSearch } from "_graphql/cache/auth";
import { useEffect, useState } from "react";

import { ReopenOfferForm } from "components/forms";
import { addPrecentageToParticipant } from "_graphql/kek/mutation/index.kek";
import toast from "react-hot-toast";
import { useFormik } from "formik";
import { useKEKOffer } from "hooks/data/use-offers";
import { useMutation } from "@apollo/client";
import { wrapClick } from "utils";

type Props = {
  open: boolean;
  setOpen: (open: boolean) => void;
};

const validationSchema = Yup.object().shape({
  participants: Yup.array().of(
    Yup.object().shape({
      participation: Yup.number()
        .required("Participation percentage is required")
        .min(0.1, "Participation must be at least 0.1%")
        .max(100, "Participation cannot exceed 100%"),
    })
  ),
});

function ReopenOfferContainer({ open, setOpen }: Props) {
  const searchParams = useAppSearch();
  const { offer, loading } = useKEKOffer({ offer_id: searchParams?.id || "" });
  const [originalParticipants, setOriginalParticipants] = useState<any[]>([]);
  const [addPercentageToParticipant, { loading: creating }] = useMutation<
    KEKAddPercentage,
    KEKAddPercentageVariables
  >(addPrecentageToParticipant, {
    refetchQueries: ["KEKOffers"],
  });

  const form = useFormik<any>({
    initialValues: {
      participants: [],
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      // Handle form submission
      // Find the participants that have been changed
      const changedParticipants = values.participants.filter((p: any) => {
        // Find the original participant with the same ID
        const original = originalParticipants.find((orig) => orig.id === p.id);
        // Compare the participation percentages
        // Use parseFloat to handle potential string conversions
        return (
          original &&
          parseFloat(original.participation) !== parseFloat(p.participation)
        );
      });

      if (changedParticipants.length > 0) {
        console.log("Changed Participants:", changedParticipants);
        // Here you can send the changed data to your backend
        // or perform any other action with the updated list.
        addPercentageToParticipant({
          variables: {
            offer_participant_id: changedParticipants[0].id,
            offer_id: searchParams?.id || "",
            percentage: parseFloat(changedParticipants[0].participation),
            reopen: true,
          },
        })
          .then(({ data }) => {
            if (data?.addPrecentageToParticipant) {
              toast.success("Offer Reopened Successfully");
              setOpen(false);
            }
          })
          .catch((error) => {
            console.error("Error adding percentage to participant:", error);
          });
      } else {
        toast.error("No changes detected in participants");
      }
    },
    onReset: () => {},
  });

  useEffect(() => {
    if (!loading && offer) {
      const initialParticipants =
        offer?.offer_participant?.map((p) => ({
          id: p?.offer_participant_id, // Use a unique ID for comparison
          name: p?.reinsurer?.re_company_name || "",
          participation: p?.offer_participant_percentage?.toString() || "",
        })) || [];

      setOriginalParticipants(initialParticipants);
      form.setValues({
        offered: offer?.facultative_offer || 0,
        co_insurance: offer?.co_insurance_share || 0,
        participants: initialParticipants,
      });
    }
  }, [offer]);

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      loading={loading}
      // size="6xl"
      title="Reopen Offer"
      description="This action would reopen the offer for editing. To proceed, click the 'Reopen Offer' button below."
      renderActions={() => (
        <OfficeButton
          onClick={wrapClick(form.handleSubmit)}
          disabled={creating}
        >
          {creating ? "Reopening Offer..." : "Reopen Offer"}
        </OfficeButton>
      )}
    >
      <ReopenOfferForm form={form} />
    </Modal>
  );
}

export default ReopenOfferContainer;
